import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Search, ShoppingBag } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { SectionHeader } from '@/components/ui/section-header';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorState } from '@/components/ui/error-state';
import { cn } from '@/lib/utils';
import { form_error_message } from '@/lib/forms';
import { use_shop_food, use_shop_pharmacy } from '@/features/shop/hooks';
import type { ShopVertical } from '@/features/shop/types';
import { FOOD_CATEGORY_OPTIONS } from '@/features/commerce/types';
import { PHARMACY_CATEGORY_OPTIONS } from '@/features/pharmacy-catalog/types';
import { ShopProductCard } from '@/components/shop-product-card';
import { CommerceCartSheet } from '@/components/commerce-cart-sheet';
import { SpeciesFilterChips } from '@/components/species-chips';
import { SPECIES_OPTIONS, type PetSpecies } from '@/features/pet/types';

const TABS: { value: ShopVertical; label: string }[] = [
  { value: 'food', label: 'Pet food' },
  { value: 'pharmacy', label: 'Pharmacy' },
];

const CATEGORIES: Record<ShopVertical, { value: string; label: string }[]> = {
  food: [{ value: '', label: 'All' }, ...FOOD_CATEGORY_OPTIONS],
  pharmacy: [{ value: '', label: 'All' }, ...PHARMACY_CATEGORY_OPTIONS],
};

export function ShopPage() {
  // Openable from elsewhere with a tab and a query already chosen — search
  // results link here, and landing on an unfiltered shop would lose the product
  // the shopper just tapped. Read once, as the initial state: after that the
  // controls own it, so typing isn't fighting the URL.
  const [params] = useSearchParams();
  const [vertical, set_vertical] = useState<ShopVertical>(
    params.get('vertical') === 'pharmacy' ? 'pharmacy' : 'food',
  );
  const [category, set_category] = useState('');
  const [q, set_q] = useState(params.get('q') ?? '');
  // Client change, 9 Oct 2026: pick a pet so Dog food is not shown for a Bird. Kept across
  // the food/pharmacy tabs, because the shopper's pet doesn't change when the aisle does.
  const [species, set_species] = useState<PetSpecies | ''>(() => {
    const s = params.get('species');
    return SPECIES_OPTIONS.some((o) => o.value === s) ? (s as PetSpecies) : '';
  });

  const query = useMemo(
    () => ({ category: category || undefined, q: q.trim() || undefined, species: species || undefined }),
    [category, q, species],
  );
  const food_q = use_shop_food(query, vertical === 'food');
  const pharm_q = use_shop_pharmacy(query, vertical === 'pharmacy');
  const active = vertical === 'food' ? food_q : pharm_q;

  const items = active.data?.items ?? [];

  function switch_tab(v: ShopVertical) {
    set_vertical(v);
    set_category('');
    set_q('');
  }

  return (
    <div className="flex flex-col gap-5 pb-28">
      <SectionHeader
        title="Shop"
        description="Food & pharmacy essentials for your pet — delivered. Add anything to one cart and check out together."
      />

      {/* Vertical tabs */}
      <div className="flex gap-2 rounded-card border-2 border-brand-outline-variant/15 bg-secondary/30 p-1" data-testid="shop-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={vertical === t.value}
            onClick={() => switch_tab(t.value)}
            data-testid={`shop-tab-${t.value}`}
            className={cn(
              'flex-1 rounded-[calc(var(--radius-card)-0.25rem)] px-4 py-2 text-sm font-semibold transition-colors',
              vertical === t.value ? 'bg-card text-foreground shadow-clay-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => set_q(e.target.value)}
          placeholder="Search products…"
          className="pl-9"
          data-testid="shop-search"
        />
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap gap-2" data-testid="shop-category-chips">
        {CATEGORIES[vertical].map((c) => (
          <button
            key={c.value || 'all'}
            type="button"
            onClick={() => set_category(c.value)}
            data-testid={`shop-category-${c.value || 'all'}`}
            data-active={category === c.value}
            className={cn(
              'rounded-full border-2 px-3.5 py-1.5 text-sm font-semibold transition-colors',
              // Solid tokens: the old bg-primary/10 compiled to nothing, so "selected" barely showed.
              category === c.value
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-card text-muted-foreground hover:border-primary hover:text-foreground',
            )}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Pet filter — products with no pet set are for all pets and always shown. */}
      <SpeciesFilterChips value={species} onChange={set_species} testid="shop-species" />

      {/* Grid */}
      {active.isLoading ? (
        <div className="flex items-center justify-center p-12 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading products…
        </div>
      ) : active.isError ? (
        <ErrorState
          data-testid="shop-error"
          title="Couldn't load products"
          description={form_error_message(active.error, 'Please try again.')}
        />
      ) : items.length === 0 ? (
        <EmptyState
          data-testid="shop-empty"
          icon={ShoppingBag}
          title="No products found"
          description={q || category || species ? 'Try a different search, category or pet.' : 'Check back soon — new products are on the way.'}
        />
      ) : (
        <div
          className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4"
          data-testid="shop-grid"
        >
          {items.map((p) => (
            <ShopProductCard
              key={p.id}
              product={p}
              vertical={vertical === 'food' ? 'FOOD' : 'PHARMACY'}
            />
          ))}
        </div>
      )}

      <CommerceCartSheet />
    </div>
  );
}
